import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { JumpToViewWord } from '@components/editors/jump-to-view-word.tsx';
import cn from 'classnames';
import {
  Components as VirtuosoComponents,
  Virtuoso,
  VirtuosoHandle
} from 'react-virtuoso';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { getCurrentParagraphSeeking } from '@utils/editors/get-current-paragraph-seeking.ts';
import { indexOf } from 'lodash';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { SharedTranscriptRow } from '@components/editors/shared-editor/shared-transcript/shared-transcript-row.tsx';
import { SharedEditorProvider } from '@utils/providers/shared-editor-context.tsx';
import { SharedTranscriptSearch } from '@utils/api/magic-links/shared-transcript-search.tsx';
import SharedTranscriptKeyboardInfo from '@utils/api/magic-links/shared-transcript-keyboard-info.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { SharedTranscriptActionButtons } from '@components/editors/shared-editor/shared-transcript/shared-transcript-action-buttons.tsx';
import { SharedTranscriptTextStyleBox } from '@components/editors/shared-editor/shared-transcript/shared-transcript-text-style-box.tsx';
import { SharedTranscriptMediaPlayer } from '@components/editors/shared-editor/shared-transcript/shared-transcript-media-player.tsx';

export const SharedTranscriptBody = () => {
  const { currentTime } = useAudio();
  const {
    document: td,
    setShowJumpButton,
    showJumpButton,
    documentResult,
    popoverOpen,
    setPopoverOpen,
    documentVersion,
    setShowInfo
  } = useSharedTranscript();

  const currentWordRef = useRef<HTMLSpanElement | null>(null);
  const [enabled, setEnabled] = useState<boolean>(
    documentResult?.result?.finished ?? false
  );
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { vars, cls } = useThemeVars();
  const { getColour } = useThemeContext();

  useEffect(() => {
    if (popoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [popoverOpen]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setPopoverOpen(false);
    }
  };

  const onWordOutOfView = (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => {
    currentWordRef.current = ref;
    setShowJumpButton(direction);
  };

  const onWordInView = () => {
    setShowJumpButton(false);
  };

  const getEditorValue = useMemo(() => {
    if (!td || !td.document) return [];
    return td.document.map((paragraph, index) => ({
      ...paragraph,
      id: index
    }));
  }, [td]);

  const jumpToCurrentWord = useCallback(() => {
    const item = getCurrentParagraphSeeking(getEditorValue, currentTime);
    if (item && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: indexOf(getEditorValue, item),
        align: 'center',
        behavior: 'smooth'
      });
    }
    setShowJumpButton(false);
  }, [getEditorValue, currentTime]);

  const renderRow = useCallback(
    (index: number) => {
      const paragraph = getEditorValue[index];
      return (
        <SharedTranscriptRow
          key={`row-v${documentVersion}-${paragraph.start}-${paragraph.stop}`}
          index={index}
          paragraph={paragraph}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
        />
      );
    },
    [getEditorValue, onWordOutOfView, onWordInView, documentVersion]
  );

  const vComponents: VirtuosoComponents = useMemo(
    () => ({
      Item: ({ children, ...props }) => (
        <div {...props} style={{ padding: '1rem' }}>
          {children}
        </div>
      )
    }),
    []
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const { start, stop } = (e as CustomEvent).detail || {};
      if (start === undefined || stop === undefined) return;

      const idx = getEditorValue.findIndex(
        p => (p.start ?? 0) <= start && (p.stop ?? 0) >= stop
      );
      if (idx >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: idx,
          align: 'center',
          behavior: 'smooth'
        });
      }
    };

    window.addEventListener('transcript:focusMatch', handler as EventListener);
    return () =>
      window.removeEventListener(
        'transcript:focusMatch',
        handler as EventListener
      );
  }, [getEditorValue]);

  return (
    <SharedEditorProvider>
      <div
        className={cn('h-full flex flex-col px-4', cls.pageBg, cls.text)}
        style={vars}
      >
        {/* Content area — at >= 1223px, side-by-side 11-col grid.
            At <= 1222px, stacks into two rows: editor on top, side panel below. */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 min-[1223px]:grid min-[1223px]:grid-cols-11 min-[1223px]:grid-rows-1">
          {/* Main editor (left) */}
          <div className="flex-1 min-h-0 overflow-hidden min-[1223px]:col-span-9">
            <section
              aria-labelledby="section-1-title"
              className="flex flex-col h-full"
            >
              <div
                className="relative flex-1 min-h-0 m-2 flex flex-col rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                {/* Header — action buttons (left) + text-style box (right),
                    mirrors the private transcript-body editor header. */}
                <header
                  className="flex items-center justify-between gap-2 px-3 py-1 h-14 shrink-0 border-b overflow-x-auto"
                  style={{ borderColor: getColour('border') }}
                >
                  <SharedTranscriptActionButtons
                    setShowInfo={setShowInfo}
                    enabled={enabled}
                    setEnabled={setEnabled}
                  />
                  <SharedTranscriptTextStyleBox
                    containerRef={containerRef}
                    popoverOpen={popoverOpen}
                  />
                </header>

                <div className="flex-1 text-lg overflow-auto">
                  <Virtuoso
                    ref={virtuosoRef}
                    totalCount={getEditorValue.length}
                    style={{
                      height: '100%',
                      width: '100%',
                      overflowX: 'hidden'
                    }}
                    components={vComponents}
                    itemContent={renderRow}
                    // Cap initial synchronous mount — each row is a full Slate
                    // editor; the whole length mounted every editor at once.
                    // (See caption-main.tsx.)
                    initialItemCount={Math.min(getEditorValue.length, 20)}
                    increaseViewportBy={200}
                  />
                </div>
              </div>

              <JumpToViewWord
                onJumpToWord={jumpToCurrentWord}
                visible={showJumpButton}
              />
            </section>
          </div>

          {/* Side panel (right) — Search + Media inside one rounded card.
              Stacked below 1223px, side column at >= 1223px. */}
          <div className="min-h-0 flex-1 overflow-hidden flex flex-col min-[1223px]:flex-none min-[1223px]:col-span-2 min-[1223px]:h-full">
            <div
              className="relative flex-1 min-h-0 m-2 flex flex-col rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('firstLayer')
              }}
            >
              <div
                className="min-h-0 basis-1/2 shrink-0 grow-0 overflow-hidden border-b"
                style={{ borderColor: getColour('border') }}
              >
                <SharedTranscriptSearch />
              </div>
              <div className="min-h-0 basis-1/2 shrink-0 grow-0 overflow-hidden">
                <SharedTranscriptMediaPlayer />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SharedTranscriptKeyboardInfo />
    </SharedEditorProvider>
  );
};
